import React, { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import api from '../services/api';

const CATEGORIES = [
  'Autre', 'Climatisation', 'Distribution', 'Embrayage',
  'Freinage', 'Géométrie', 'Pneumatiques', 'Révision'
];

const STATUTS_FORFAIT = [
  { value: 'actif', label: 'Actif', color: 'var(--color-success)' },
  { value: 'a_modifier', label: 'A modifier', color: 'var(--color-warning)' },
  { value: 'a_supprimer', label: 'A supprimer', color: 'var(--color-error)' },
];

const CODES_TARIF_MO = ['GAR', 'T1', 'T2', 'T3'];

function formatPrix(v) {
  const n = parseFloat(v) || 0;
  return n.toFixed(2).replace('.', ',') + ' €';
}

// Lit les tokens CSS (couleurs) pour les graphiques — réactif au dark mode.
function useChartColors() {
  const read = () => {
    const cs = getComputedStyle(document.documentElement);
    const v = (name, fb) => (cs.getPropertyValue(name).trim() || fb);
    return {
      accent: v('--color-accent', '#0891b2'),
      text:   v('--text-secondary', '#64748b'),
      muted:  v('--text-muted', '#94a3b8'),
      grid:   v('--border-default', '#e2e8f0'),
      surface: v('--bg-surface', '#ffffff'),
    };
  };
  const [colors, setColors] = useState(read);
  useEffect(() => {
    const obs = new MutationObserver(() => setColors(read()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return colors;
}

// ── Comparatif Tarifs MO par code (GAR/T1/T2/T3) : une carte par code, une
// barre horizontale par tarif renseigné (libellé + concession en catégorie,
// montant en valeur). Barres horizontales : plus lisible que du vertical
// quand les libellés sont longs (pas de rotation/troncature de l'axe X).
function TarifMoCard({ code, rows, colors, tooltipStyle }) {
  const chartData = rows
    .map(r => ({
      yLabel: `${r.concession_nom || '—'}|||${r.libelle || ''}`,
      concessionNom: r.concession_nom || '—',
      libelle: r.libelle || '',
      montant: parseFloat(r.tarif_ht) || 0,
      date_tarif: r.date_tarif,
    }))
    .sort((a, b) => b.montant - a.montant);

  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 10, padding: 16 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 700 }}>{code}</h3>
      {chartData.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-muted)', fontSize: 13 }}>
          Aucun tarif « {code} » renseigné
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={Math.max(90, chartData.length * 46 + 20)}>
          <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 130, left: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: colors.muted }} axisLine={false} tickLine={false} tickFormatter={v => `${v}€`} />
            <YAxis
              type="category"
              dataKey="yLabel"
              width={150}
              axisLine={false}
              tickLine={false}
              tick={(props) => {
                const { x, y, payload } = props;
                const [concession, libelle] = String(payload.value).split('|||');
                return (
                  <g transform={`translate(${x},${y})`}>
                    <text x={-6} y={libelle ? -4 : 0} dy={4} textAnchor="end" fontSize={11} fontWeight={600} fill={colors.text}>
                      {concession}
                    </text>
                    {libelle && (
                      <text x={-6} y={9} dy={4} textAnchor="end" fontSize={10} fill={colors.muted}>
                        {libelle.length > 26 ? libelle.slice(0, 24) + '…' : libelle}
                      </text>
                    )}
                  </g>
                );
              }}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              cursor={{ fill: colors.grid, opacity: 0.25 }}
              labelFormatter={(_, p) => {
                const row = p?.[0]?.payload;
                return row ? `${row.concessionNom}${row.libelle ? ' — ' + row.libelle : ''}` : '';
              }}
              formatter={(val, name, props) => [
                `${formatPrix(val)}${props.payload.date_tarif ? ' · maj ' + new Date(props.payload.date_tarif).toLocaleDateString('fr-FR') : ''}`,
                'Tarif HT',
              ]}
            />
            <Bar dataKey="montant" fill={colors.accent} radius={[0, 4, 4, 0]} maxBarSize={26}>
              <LabelList
                dataKey="montant"
                position="right"
                content={(props) => {
                  const { x, y, width, height, index } = props;
                  const row = chartData[index];
                  if (!row) return null;
                  const label = row.date_tarif
                    ? `${formatPrix(row.montant)}  ·  ${new Date(row.date_tarif).toLocaleDateString('fr-FR')}`
                    : formatPrix(row.montant);
                  return (
                    <text x={x + width + 6} y={y + height / 2} dy={4} fontSize={11} fontWeight={600} fill={colors.text}>
                      {label}
                    </text>
                  );
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function TarifMoComparatif({ tarifsMo, colors }) {
  const tooltipStyle = {
    background: colors.surface, border: `1px solid ${colors.grid}`,
    borderRadius: 8, fontSize: 12, color: colors.text, boxShadow: 'var(--shadow-lg)',
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 16, marginBottom: 20 }}>
      {CODES_TARIF_MO.map(code => (
        <TarifMoCard key={code} code={code} rows={tarifsMo.filter(t => t.code === code)} colors={colors} tooltipStyle={tooltipStyle} />
      ))}
    </div>
  );
}

function Forfaits({ user, setHeaderAction }) {
  const chartColors = useChartColors();
  const [activeTab, setActiveTab] = useState('forfaits'); // 'forfaits' | 'tarifs-mo' | 'concurrence'
  const [forfaits, setForfaits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [filterSegment, setFilterSegment] = useState('');
  const [filterMarque, setFilterMarque] = useState('');
  const [filterModele, setFilterModele] = useState('');
  const [filterConcession, setFilterConcession] = useState('');
  const [concessions, setConcessions] = useState([]);
  const [marques, setMarques] = useState([]);
  const [modeles, setModeles] = useState([]);

  // Tarifs MO
  const [tarifsMo, setTarifsMo] = useState([]);
  const [tarifsMoLoading, setTarifsMoLoading] = useState(false);
  const [newTarifMo, setNewTarifMo] = useState(null);
  const [editTarifMo, setEditTarifMo] = useState(null);

  // Tarifs concurrence
  const [tarifsConcurrence, setTarifsConcurrence] = useState([]);
  const [tarifsConLoading, setTarifsConLoading] = useState(false);
  const [newTarifCon, setNewTarifCon] = useState(null);
  const [editTarifCon, setEditTarifCon] = useState(null);

  // Fiche détail
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [historique, setHistorique] = useState([]);

  // Nouveau forfait
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ nom: '', categorie: '', code_forfait: '', segment: '', statut: 'actif', prix_vente_ht: '', prix_vente_ttc: '', concession_id: '' });

  // Nouvelle ligne
  const [newLigne, setNewLigne] = useState(null); // {type, designation, reference, prix_achat_ht, quantite}

  // Édition inline
  const [editCell, setEditCell] = useState(null); // {ligneId, field}
  const editRef = useRef(null);

  // Édition inline dans le tableau principal (segment)
  const [editListCell, setEditListCell] = useState(null); // {id, field}

  // Header action
  useEffect(() => {
    if (setHeaderAction) {
      setHeaderAction({
        primary: detail ? null : { label: '+ Nouveau forfait', onClick: () => setShowNew(true) }
      });
    }
  }, [setHeaderAction, detail]);

  // Chargement initial
  useEffect(() => {
    loadForfaits();
    api.get('/concessions').then(r => setConcessions(r.data.concessions || r.data || [])).catch(() => {});
    api.get('/referentiels/marques').then(r => setMarques(r.data.marques || [])).catch(() => {});
    api.get('/referentiels/modeles').then(r => setModeles(r.data.modeles || [])).catch(() => {});
  }, []); // eslint-disable-line

  const loadForfaits = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterConcession) params.concession_id = filterConcession;
      if (filterCat) params.categorie = filterCat;
      const res = await api.get('/forfaits', { params });
      setForfaits(res.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { loadForfaits(); }, [filterCat, filterConcession]); // eslint-disable-line

  // Charger tarifs MO
  const loadTarifsMo = async () => {
    setTarifsMoLoading(true);
    try {
      const res = await api.get('/forfaits/tarifs-mo/list');
      setTarifsMo(res.data || []);
    } catch (e) { console.error(e); }
    setTarifsMoLoading(false);
  };

  // Charger tarifs concurrence
  const loadTarifsConcurrence = async () => {
    setTarifsConLoading(true);
    try {
      const res = await api.get('/forfaits/concurrence/list');
      setTarifsConcurrence(res.data || []);
    } catch (e) { console.error(e); }
    setTarifsConLoading(false);
  };

  useEffect(() => {
    if (activeTab === 'tarifs-mo') loadTarifsMo();
    if (activeTab === 'concurrence') loadTarifsConcurrence();
  }, [activeTab]); // eslint-disable-line

  // Ouvrir un forfait
  const openDetail = async (id) => {
    setDetailLoading(true);
    try {
      const [fRes, hRes] = await Promise.all([
        api.get(`/forfaits/${id}`),
        api.get(`/forfaits/${id}/historique`)
      ]);
      setDetail(fRes.data);
      setHistorique(hRes.data || []);
    } catch (e) { console.error(e); }
    setDetailLoading(false);
  };

  const closeDetail = () => {
    setDetail(null);
    setHistorique([]);
    setEditCell(null);
    setNewLigne(null);
    loadForfaits();
  };

  // Créer un forfait
  const handleCreate = async () => {
    if (!newForm.nom.trim()) return;
    try {
      const res = await api.post('/forfaits', newForm);
      setShowNew(false);
      setNewForm({ nom: '', categorie: '', code_forfait: '', segment: '', statut: 'actif', prix_vente_ht: '', prix_vente_ttc: '', concession_id: '' });
      openDetail(res.data.id);
      loadForfaits();
    } catch (e) { alert(e.response?.data?.error || 'Erreur'); }
  };

  // Modifier le forfait (header)
  const updateForfait = async (field, value) => {
    if (!detail) return;
    try {
      const res = await api.put(`/forfaits/${detail.id}`, { ...detail, [field]: value });
      setDetail(prev => ({ ...prev, ...res.data }));
      if (field === 'prix_vente_ht') {
        const hRes = await api.get(`/forfaits/${detail.id}/historique`);
        setHistorique(hRes.data || []);
      }
    } catch (e) { console.error(e); }
  };

  // Modifier un champ directement depuis le tableau principal (liste)
  const updateForfaitInList = async (f, field, value) => {
    setForfaits(prev => prev.map(x => x.id === f.id ? { ...x, [field]: value } : x));
    try {
      await api.put(`/forfaits/${f.id}`, { ...f, [field]: value });
    } catch (e) {
      console.error(e);
      setForfaits(prev => prev.map(x => x.id === f.id ? { ...x, [field]: f[field] } : x));
    }
  };

  // Ajouter une ligne
  const handleAddLigne = async () => {
    if (!newLigne?.designation?.trim()) return;
    try {
      const res = await api.post(`/forfaits/${detail.id}/lignes`, newLigne);
      setDetail(prev => ({ ...prev, lignes: [...prev.lignes, res.data] }));
      setNewLigne(null);
    } catch (e) { console.error(e); }
  };

  // Modifier une ligne inline
  const handleUpdateLigne = async (ligneId, field, value) => {
    try {
      const res = await api.put(`/forfaits/${detail.id}/lignes/${ligneId}`, { [field]: value });
      setDetail(prev => ({
        ...prev,
        lignes: prev.lignes.map(l => l.id === ligneId ? { ...l, ...res.data } : l)
      }));
      if (field === 'prix_achat_ht') {
        const hRes = await api.get(`/forfaits/${detail.id}/historique`);
        setHistorique(hRes.data || []);
      }
    } catch (e) { console.error(e); }
    setEditCell(null);
  };

  // Supprimer une ligne
  const handleDeleteLigne = async (ligneId) => {
    try {
      await api.delete(`/forfaits/${detail.id}/lignes/${ligneId}`);
      setDetail(prev => ({ ...prev, lignes: prev.lignes.filter(l => l.id !== ligneId) }));
    } catch (e) { console.error(e); }
  };

  // Supprimer un forfait
  const handleDelete = async () => {
    if (!window.confirm('Supprimer ce forfait ?')) return;
    await api.delete(`/forfaits/${detail.id}`);
    closeDetail();
  };

  // Marques / Modèles — plusieurs paires (marque, modèle optionnel) par forfait.
  // Sauvegarde immédiate (remplacement complet côté serveur) à chaque changement ;
  // les lignes sans marque sélectionnée (en cours de saisie) ne sont pas envoyées.
  const saveMarquesModeles = async (list) => {
    const valid = list.filter(m => m.marque_id);
    await api.put(`/forfaits/${detail.id}/marques`, {
      marques: valid.map(m => ({ marque_id: m.marque_id, modele_id: m.modele_id || null })),
    });
    setDetail(prev => ({
      ...prev,
      marques: list.map(m => ({
        ...m,
        marque_nom: marques.find(ma => ma.id === m.marque_id)?.nom || '',
        modele_nom: modeles.find(mo => mo.id === m.modele_id)?.nom || '',
      })),
    }));
  };

  const addMarqueModeleLigne = () => {
    setDetail(prev => ({ ...prev, marques: [...(prev.marques || []), { marque_id: null, modele_id: null }] }));
  };

  const updateMarqueModeleLigne = (index, field, value) => {
    const current = [...(detail.marques || [])];
    current[index] = { ...current[index], [field]: value };
    if (field === 'marque_id') current[index].modele_id = null; // reset modèle si la marque change
    if (current[index].marque_id) saveMarquesModeles(current);
    else setDetail(prev => ({ ...prev, marques: current })); // ligne encore incomplète, pas de sauvegarde
  };

  const removeMarqueModeleLigne = (index) => {
    const current = (detail.marques || []).filter((_, i) => i !== index);
    saveMarquesModeles(current);
  };

  // Création d'un modèle à la volée depuis le sélecteur (alimente le référentiel
  // `modeles`). Ouverte à qui peut CRÉER sur cette page, plus les administrateurs :
  // exiger un accès à Administration pour ajouter un modèle obligeait à ouvrir
  // bien plus large que nécessaire. La suppression, elle, reste réservée aux admins.
  const [newModeleDraft, setNewModeleDraft] = useState(null); // { index, marqueId, nom }
  const createModeleInline = async () => {
    if (!newModeleDraft?.nom?.trim()) return;
    try {
      const res = await api.post('/referentiels/modeles', { nom: newModeleDraft.nom.trim(), marque_id: newModeleDraft.marqueId });
      const created = res.data.modele;
      setModeles(prev => [...prev, created]);
      updateMarqueModeleLigne(newModeleDraft.index, 'modele_id', created.id);
    } catch (err) {
      alert(err.response?.data?.error || "Erreur lors de la création du modèle");
    } finally {
      setNewModeleDraft(null);
    }
  };

  // Calculs
  const coutTotal = detail ? detail.lignes.reduce((s, l) => s + (parseFloat(l.prix_achat_ht) || 0) * (parseFloat(l.quantite) || 1), 0) : 0;

  // Listes de valeurs distinctes (segment/marque/modèle) pour les filtres — dérivées
  // des forfaits chargés, pas d'appel serveur dédié (mêmes données que le tableau).
  const segmentsDisponibles = Array.from(new Set(forfaits.map(f => f.segment).filter(Boolean))).sort();
  const marquesDisponibles = Array.from(new Set(forfaits.flatMap(f => f.marques_noms || []))).sort();
  const modelesDisponibles = Array.from(new Set(forfaits.flatMap(f => f.modeles_noms || []))).sort();

  // Filtrage liste
  const filtered = forfaits.filter(f => {
    if (search) {
      const q = search.toLowerCase();
      if (!f.nom.toLowerCase().includes(q) && !(f.categorie || '').toLowerCase().includes(q)) return false;
    }
    if (filterSegment && f.segment !== filterSegment) return false;
    if (filterMarque && !(f.marques_noms || []).includes(filterMarque)) return false;
    if (filterModele && !(f.modeles_noms || []).includes(filterModele)) return false;
    return true;
  });

  // Tri liste — clic sur un en-tête de colonne
  const getSortValue = (f, key) => {
    switch (key) {
      case 'nom':           return (f.nom || '').toLowerCase();
      case 'code_forfait':  return (f.code_forfait || '').toLowerCase();
      case 'segment':       return (f.segment || '').toLowerCase();
      case 'categorie':     return (f.categorie || '').toLowerCase();
      case 'statut':        return f.statut || 'actif';
      case 'concession_nom': return (f.concession_nom || '').toLowerCase();
      case 'marques':       return (f.marques_noms || []).join(', ').toLowerCase();
      case 'cout':          return parseFloat(f.cout_total) || 0;
      case 'prix_ht':       return parseFloat(f.prix_vente_ht) || 0;
      case 'prix_ttc':      return parseFloat(f.prix_vente_ttc) || 0;
      case 'marge': {
        const cout = parseFloat(f.cout_total) || 0;
        const vente = parseFloat(f.prix_vente_ht) || 0;
        return vente > 0 ? ((vente - cout) / vente * 100) : 0;
      }
      default: return '';
    }
  };
  const sorted = sortKey ? [...filtered].sort((a, b) => {
    const av = getSortValue(a, sortKey), bv = getSortValue(b, sortKey);
    if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortDir === 'asc' ? av - bv : bv - av;
  }) : filtered;
  const onSort = (key) => {
    if (key === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };
  const sortArrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  // ═══ FICHE DÉTAIL ═══
  if (detail) {
    return (
      <div className="page-container">
        <div className="page-toolbar">
          <button className="page-select" onClick={closeDetail} style={{ cursor: 'pointer' }}>← Retour</button>
          <div style={{ flex: 1 }} />
          <button className="page-select" style={{ color: 'var(--color-error)', cursor: 'pointer' }} onClick={handleDelete}>🗑️ Supprimer</button>
        </div>

        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Chargement...</div>
        ) : (
          <>
            {/* En-tête forfait */}
            <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-table)', padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Nom du forfait</label>
                  <input
                    type="text"
                    value={detail.nom}
                    onChange={e => setDetail(prev => ({ ...prev, nom: e.target.value }))}
                    onBlur={e => updateForfait('nom', e.target.value)}
                    style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 14, fontWeight: 600 }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Catégorie</label>
                  <select
                    value={detail.categorie || ''}
                    onChange={e => { setDetail(prev => ({ ...prev, categorie: e.target.value })); updateForfait('categorie', e.target.value); }}
                    style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13 }}
                  >
                    <option value="">—</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Prix de vente HT</label>
                  <input
                    type="number"
                    step="0.01"
                    value={detail.prix_vente_ht}
                    onChange={e => setDetail(prev => ({ ...prev, prix_vente_ht: e.target.value }))}
                    onBlur={e => updateForfait('prix_vente_ht', parseFloat(e.target.value) || 0)}
                    style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 14, fontWeight: 600 }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Prix de vente TTC</label>
                  <input
                    type="number"
                    step="0.01"
                    value={detail.prix_vente_ttc ?? ''}
                    onChange={e => setDetail(prev => ({ ...prev, prix_vente_ttc: e.target.value }))}
                    onBlur={e => updateForfait('prix_vente_ttc', e.target.value === '' ? null : parseFloat(e.target.value) || 0)}
                    style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 14, fontWeight: 600 }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Code forfait</label>
                  <input
                    type="text"
                    value={detail.code_forfait || ''}
                    onChange={e => setDetail(prev => ({ ...prev, code_forfait: e.target.value }))}
                    onBlur={e => updateForfait('code_forfait', e.target.value || null)}
                    style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 14, fontWeight: 600 }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Segment</label>
                  <input
                    type="text"
                    value={detail.segment || ''}
                    onChange={e => setDetail(prev => ({ ...prev, segment: e.target.value }))}
                    onBlur={e => updateForfait('segment', e.target.value || null)}
                    style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 14, fontWeight: 600 }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Statut</label>
                  <select
                    value={detail.statut || 'actif'}
                    onChange={e => { setDetail(prev => ({ ...prev, statut: e.target.value })); updateForfait('statut', e.target.value); }}
                    style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13 }}
                  >
                    {STATUTS_FORFAIT.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Marques / Modèles */}
              <div style={{ marginTop: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Marques / Modèles associés</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {(detail.marques || []).map((fm, idx) => {
                    const modelesDeLaMarque = modeles.filter(mo => mo.marque_id === fm.marque_id);
                    return (
                      <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <select
                          value={fm.marque_id || ''}
                          onChange={e => updateMarqueModeleLigne(idx, 'marque_id', e.target.value ? parseInt(e.target.value, 10) : null)}
                          style={{ flex: 1, padding: '6px 8px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13, background: 'var(--bg-surface)', color: 'var(--text-primary)' }}
                        >
                          <option value="">Choisir une marque…</option>
                          {marques.map(m => <option key={m.id} value={m.id}>{m.nom}</option>)}
                        </select>
                        {newModeleDraft?.index === idx ? (
                          <div style={{ flex: 1, display: 'flex', gap: 4 }}>
                            <input
                              autoFocus
                              type="text"
                              placeholder="Nom du nouveau modèle"
                              value={newModeleDraft.nom}
                              onChange={e => setNewModeleDraft(d => ({ ...d, nom: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter') createModeleInline(); if (e.key === 'Escape') setNewModeleDraft(null); }}
                              style={{ flex: 1, padding: '6px 8px', border: '1px solid var(--color-accent)', borderRadius: 6, fontSize: 13 }}
                            />
                            <button className="btn-icon" title="Créer" onClick={createModeleInline}>✓</button>
                            <button className="btn-icon" title="Annuler" onClick={() => setNewModeleDraft(null)}>✕</button>
                          </div>
                        ) : (
                          <select
                            value={fm.modele_id || ''}
                            onChange={e => {
                              if (e.target.value === '__new__') setNewModeleDraft({ index: idx, marqueId: fm.marque_id, nom: '' });
                              else updateMarqueModeleLigne(idx, 'modele_id', e.target.value ? parseInt(e.target.value, 10) : null);
                            }}
                            disabled={!fm.marque_id}
                            style={{ flex: 1, padding: '6px 8px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 13, background: 'var(--bg-surface)', color: 'var(--text-primary)', opacity: fm.marque_id ? 1 : 0.5 }}
                          >
                            <option value="">Tous modèles</option>
                            {modelesDeLaMarque.map(mo => <option key={mo.id} value={mo.id}>{mo.nom}</option>)}
                            {fm.marque_id && <option value="__new__">➕ Nouveau modèle…</option>}
                          </select>
                        )}
                        <button
                          className="btn-icon delete"
                          title="Supprimer"
                          onClick={() => removeMarqueModeleLigne(idx)}
                        >
                          🗑️
                        </button>
                      </div>
                    );
                  })}
                  <button
                    className="page-select"
                    style={{ cursor: 'pointer', alignSelf: 'flex-start' }}
                    onClick={addMarqueModeleLigne}
                  >
                    + Ajouter une marque / modèle
                  </button>
                </div>
              </div>
            </div>

            {/* Tableau lignes */}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 80 }}>Type</th>
                    <th>Désignation</th>
                    <th style={{ width: 120 }}>Référence</th>
                    <th style={{ width: 100, textAlign: 'right' }}>PU HT</th>
                    <th style={{ width: 60, textAlign: 'center' }}>Qté</th>
                    <th style={{ width: 100, textAlign: 'right' }}>Total</th>
                    <th style={{ width: 50 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {detail.lignes.map(l => (
                    <tr key={l.id}>
                      <td>
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
                          background: l.type === 'piece' ? '#dbeafe' : '#fef3c7',
                          color: l.type === 'piece' ? '#1d4ed8' : '#b45309'
                        }}>
                          {l.type === 'piece' ? '🔧 Pièce' : '👷 MO'}
                        </span>
                      </td>
                      <td
                        onClick={() => setEditCell({ ligneId: l.id, field: 'designation' })}
                        style={{ cursor: 'pointer' }}
                      >
                        {editCell?.ligneId === l.id && editCell?.field === 'designation' ? (
                          <input
                            ref={editRef}
                            autoFocus
                            defaultValue={l.designation}
                            onBlur={e => handleUpdateLigne(l.id, 'designation', e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                            style={{ width: '100%', padding: '2px 4px', border: '1px solid var(--color-accent)', borderRadius: 4 }}
                          />
                        ) : l.designation}
                      </td>
                      <td
                        onClick={() => setEditCell({ ligneId: l.id, field: 'reference' })}
                        style={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: 11 }}
                      >
                        {editCell?.ligneId === l.id && editCell?.field === 'reference' ? (
                          <input
                            autoFocus
                            defaultValue={l.reference || ''}
                            onBlur={e => handleUpdateLigne(l.id, 'reference', e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                            style={{ width: '100%', padding: '2px 4px', border: '1px solid var(--color-accent)', borderRadius: 4 }}
                          />
                        ) : (l.reference || <span style={{ color: 'var(--text-muted)' }}>—</span>)}
                      </td>
                      <td
                        onClick={() => setEditCell({ ligneId: l.id, field: 'prix_achat_ht' })}
                        style={{ cursor: 'pointer', textAlign: 'right' }}
                      >
                        {editCell?.ligneId === l.id && editCell?.field === 'prix_achat_ht' ? (
                          <input
                            autoFocus
                            type="number"
                            step="0.01"
                            defaultValue={l.prix_achat_ht}
                            onBlur={e => handleUpdateLigne(l.id, 'prix_achat_ht', parseFloat(e.target.value) || 0)}
                            onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                            style={{ width: 80, padding: '2px 4px', border: '1px solid var(--color-accent)', borderRadius: 4, textAlign: 'right' }}
                          />
                        ) : formatPrix(l.prix_achat_ht)}
                      </td>
                      <td
                        onClick={() => setEditCell({ ligneId: l.id, field: 'quantite' })}
                        style={{ cursor: 'pointer', textAlign: 'center' }}
                      >
                        {editCell?.ligneId === l.id && editCell?.field === 'quantite' ? (
                          <input
                            autoFocus
                            type="number"
                            step="0.5"
                            min="0.5"
                            defaultValue={l.quantite}
                            onBlur={e => handleUpdateLigne(l.id, 'quantite', parseFloat(e.target.value) || 1)}
                            onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                            style={{ width: 50, padding: '2px 4px', border: '1px solid var(--color-accent)', borderRadius: 4, textAlign: 'center' }}
                          />
                        ) : (parseFloat(l.quantite) || 1)}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>
                        {formatPrix((parseFloat(l.prix_achat_ht) || 0) * (parseFloat(l.quantite) || 1))}
                      </td>
                      <td>
                        <button className="btn-icon delete" title="Supprimer" onClick={() => handleDeleteLigne(l.id)}>🗑️</button>
                      </td>
                    </tr>
                  ))}

                  {/* Ligne d'ajout */}
                  {newLigne ? (
                    <tr style={{ background: 'var(--bg-muted)' }}>
                      <td>
                        <select value={newLigne.type} onChange={e => setNewLigne(prev => ({ ...prev, type: e.target.value }))} style={{ fontSize: 11, padding: '2px 4px', borderRadius: 4 }}>
                          <option value="piece">🔧 Pièce</option>
                          <option value="mo">👷 MO</option>
                        </select>
                      </td>
                      <td><input autoFocus placeholder="Désignation" value={newLigne.designation} onChange={e => setNewLigne(prev => ({ ...prev, designation: e.target.value }))} onKeyDown={e => e.key === 'Enter' && handleAddLigne()} style={{ width: '100%', padding: '2px 4px', border: '1px solid var(--border-default)', borderRadius: 4 }} /></td>
                      <td><input placeholder="Réf." value={newLigne.reference} onChange={e => setNewLigne(prev => ({ ...prev, reference: e.target.value }))} style={{ width: '100%', padding: '2px 4px', border: '1px solid var(--border-default)', borderRadius: 4, fontFamily: 'monospace', fontSize: 11 }} /></td>
                      <td><input type="number" step="0.01" placeholder="0,00" value={newLigne.prix_achat_ht} onChange={e => setNewLigne(prev => ({ ...prev, prix_achat_ht: e.target.value }))} style={{ width: 80, padding: '2px 4px', border: '1px solid var(--border-default)', borderRadius: 4, textAlign: 'right' }} /></td>
                      <td><input type="number" step="0.5" min="0.5" value={newLigne.quantite} onChange={e => setNewLigne(prev => ({ ...prev, quantite: e.target.value }))} style={{ width: 50, padding: '2px 4px', border: '1px solid var(--border-default)', borderRadius: 4, textAlign: 'center' }} /></td>
                      <td></td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn-icon" title="Valider" onClick={handleAddLigne}>✅</button>
                          <button className="btn-icon" title="Annuler" onClick={() => setNewLigne(null)}>✕</button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr>
                      <td colSpan={7}>
                        <button
                          onClick={() => setNewLigne({ type: 'piece', designation: '', reference: '', prix_achat_ht: '', quantite: 1 })}
                          style={{ width: '100%', padding: 8, border: '2px dashed var(--border-default)', borderRadius: 6, background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13 }}
                        >
                          + Ajouter une ligne
                        </button>
                      </td>
                    </tr>
                  )}

                  {/* Total */}
                  <tr style={{ fontWeight: 700, background: 'var(--bg-muted)' }}>
                    <td colSpan={5} style={{ textAlign: 'right' }}>Coût de revient</td>
                    <td style={{ textAlign: 'right' }}>{formatPrix(coutTotal)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Historique des prix */}
            {historique.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8 }}>Historique des prix</div>
                <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 8, padding: 12 }}>
                  {historique.map(h => (
                    <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--border-default)' }}>
                      <span style={{ color: 'var(--text-muted)', minWidth: 80 }}>{new Date(h.date_effet).toLocaleDateString('fr-FR')}</span>
                      <span style={{ fontWeight: 600 }}>{h.ligne_designation || 'Prix de vente'}</span>
                      <span style={{ color: 'var(--text-muted)' }}>{formatPrix(h.ancien_prix)} → </span>
                      <span style={{ fontWeight: 600 }}>{formatPrix(h.nouveau_prix)}</span>
                      <span style={{ color: 'var(--text-muted)', marginLeft: 'auto' }}>{[h.user_prenom, h.user_nom].filter(Boolean).join(' ')}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ═══ VUE PRINCIPALE ═══
  return (
    <div className="page-container">
      <div className="page-tabs">
        <button className={activeTab === 'forfaits' ? 'active' : ''} onClick={() => setActiveTab('forfaits')}>📋 Forfaits</button>
        <button className={activeTab === 'tarifs-mo' ? 'active' : ''} onClick={() => setActiveTab('tarifs-mo')}>👷 Tarifs MO</button>
        <button className={activeTab === 'concurrence' ? 'active' : ''} onClick={() => setActiveTab('concurrence')}>🏁 Concurrence</button>
      </div>

      {activeTab === 'forfaits' && (<>
      <div className="page-toolbar">
        <input
          type="text"
          className="page-search"
          placeholder="🔍 Rechercher..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className={`page-select${filterCat ? ' active-filter' : ''}`} value={filterCat} onChange={e => setFilterCat(e.target.value)}>
          <option value="">Toutes catégories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {segmentsDisponibles.length > 0 && (
          <select className={`page-select${filterSegment ? ' active-filter' : ''}`} value={filterSegment} onChange={e => setFilterSegment(e.target.value)}>
            <option value="">Tous segments</option>
            {segmentsDisponibles.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        {marquesDisponibles.length > 0 && (
          <select className={`page-select${filterMarque ? ' active-filter' : ''}`} value={filterMarque} onChange={e => setFilterMarque(e.target.value)}>
            <option value="">Toutes marques</option>
            {marquesDisponibles.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        )}
        {modelesDisponibles.length > 0 && (
          <select className={`page-select${filterModele ? ' active-filter' : ''}`} value={filterModele} onChange={e => setFilterModele(e.target.value)}>
            <option value="">Tous modèles</option>
            {modelesDisponibles.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        )}
        {(user.role === 'super-admin' || user.role === 'teleoperateur') && !user.concession_id && concessions.length > 1 && (
          <select className={`page-select${filterConcession ? ' active-filter' : ''}`} value={filterConcession} onChange={e => setFilterConcession(e.target.value)}>
            <option value="">Toutes concessions</option>
            {concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Chargement...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
          {forfaits.length === 0 ? 'Aucun forfait. Cliquez sur "+ Nouveau forfait" pour commencer.' : 'Aucun résultat pour cette recherche.'}
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th style={{ cursor: 'pointer' }} onClick={() => onSort('nom')}>Nom{sortArrow('nom')}</th>
                <th style={{ width: 130, cursor: 'pointer' }} onClick={() => onSort('code_forfait')}>Code forfait{sortArrow('code_forfait')}</th>
                <th style={{ width: 100, cursor: 'pointer' }} onClick={() => onSort('segment')}>Segment{sortArrow('segment')}</th>
                <th style={{ width: 130, cursor: 'pointer' }} onClick={() => onSort('categorie')}>Catégorie{sortArrow('categorie')}</th>
                <th style={{ width: 120, cursor: 'pointer' }} onClick={() => onSort('statut')}>Statut{sortArrow('statut')}</th>
                {concessions.length > 1 && <th style={{ width: 140, cursor: 'pointer' }} onClick={() => onSort('concession_nom')}>Concession{sortArrow('concession_nom')}</th>}
                <th style={{ cursor: 'pointer' }} onClick={() => onSort('marques')}>Marques{sortArrow('marques')}</th>
                <th style={{ width: 100, textAlign: 'right', cursor: 'pointer' }} onClick={() => onSort('cout')}>Coût{sortArrow('cout')}</th>
                <th style={{ width: 100, textAlign: 'right', cursor: 'pointer' }} onClick={() => onSort('prix_ht')}>Prix HT{sortArrow('prix_ht')}</th>
                <th style={{ width: 100, textAlign: 'right', cursor: 'pointer' }} onClick={() => onSort('prix_ttc')}>Prix TTC{sortArrow('prix_ttc')}</th>
                <th style={{ width: 80, textAlign: 'right', cursor: 'pointer' }} onClick={() => onSort('marge')}>Marge{sortArrow('marge')}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(f => {
                const cout = parseFloat(f.cout_total) || 0;
                const vente = parseFloat(f.prix_vente_ht) || 0;
                const m = vente - cout;
                const pct = vente > 0 ? (m / vente * 100) : 0;
                const statutInfo = STATUTS_FORFAIT.find(s => s.value === (f.statut || 'actif')) || STATUTS_FORFAIT[0];
                return (
                  <tr key={f.id} style={{ cursor: 'pointer' }} onClick={() => openDetail(f.id)}>
                    <td style={{ fontWeight: 600 }}>{f.nom}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{f.code_forfait || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td
                      onClick={e => { e.stopPropagation(); setEditListCell({ id: f.id, field: 'segment' }); }}
                      style={{ cursor: 'pointer' }}
                    >
                      {editListCell?.id === f.id && editListCell?.field === 'segment' ? (
                        <input
                          autoFocus
                          defaultValue={f.segment || ''}
                          onBlur={e => { updateForfaitInList(f, 'segment', e.target.value || null); setEditListCell(null); }}
                          onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                          style={{ width: '100%', padding: '2px 4px', border: '1px solid var(--color-accent)', borderRadius: 4 }}
                        />
                      ) : (f.segment || <span style={{ color: 'var(--text-muted)' }}>—</span>)}
                    </td>
                    <td>
                      {f.categorie ? (
                        <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: 'var(--bg-muted)', color: 'var(--text-secondary)', fontWeight: 600 }}>
                          {f.categorie}
                        </span>
                      ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <select
                        value={f.statut || 'actif'}
                        onChange={e => updateForfaitInList(f, 'statut', e.target.value)}
                        style={{
                          border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 600, fontSize: 12,
                          color: statutInfo.color, appearance: 'none', WebkitAppearance: 'none',
                        }}
                      >
                        {STATUTS_FORFAIT.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </td>
                    {concessions.length > 1 && <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{f.concession_nom || '—'}</td>}
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{(f.marques_noms || []).join(', ') || 'Tous véhicules'}</td>
                    <td style={{ textAlign: 'right' }}>{formatPrix(cout)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatPrix(vente)}</td>
                    <td style={{ textAlign: 'right' }}>{f.prix_vente_ttc != null ? formatPrix(f.prix_vente_ttc) : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: m >= 0 ? 'var(--color-success)' : 'var(--color-error)' }}>
                      {pct.toFixed(0)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modale nouveau forfait */}
      {showNew && (
        <div className="modal-overlay">
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nouveau forfait</h2>
              <button className="modal-close" onClick={() => setShowNew(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Nom *</label>
                  <input type="text" placeholder="Ex: Forfait Révision 10 000km" value={newForm.nom} onChange={e => setNewForm(f => ({ ...f, nom: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6 }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Catégorie</label>
                  <select value={newForm.categorie} onChange={e => setNewForm(f => ({ ...f, categorie: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6 }}>
                    <option value="">—</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Prix de vente HT</label>
                  <input type="number" step="0.01" placeholder="0,00" value={newForm.prix_vente_ht} onChange={e => setNewForm(f => ({ ...f, prix_vente_ht: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6 }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Prix de vente TTC</label>
                  <input type="number" step="0.01" placeholder="0,00" value={newForm.prix_vente_ttc} onChange={e => setNewForm(f => ({ ...f, prix_vente_ttc: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6 }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Code forfait</label>
                  <input type="text" placeholder="Ex: REV10000" value={newForm.code_forfait} onChange={e => setNewForm(f => ({ ...f, code_forfait: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6 }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Segment</label>
                  <input type="text" placeholder="Ex: A, B, C..." value={newForm.segment} onChange={e => setNewForm(f => ({ ...f, segment: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6 }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Statut</label>
                  <select value={newForm.statut} onChange={e => setNewForm(f => ({ ...f, statut: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6 }}>
                    {STATUTS_FORFAIT.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                {concessions.length > 1 && (
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Concession</label>
                    <select value={newForm.concession_id} onChange={e => setNewForm(f => ({ ...f, concession_id: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: 6 }}>
                      <option value="">— Sélectionner —</option>
                      {concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowNew(false)}>Annuler</button>
              <button className="btn-primary" onClick={handleCreate} disabled={!newForm.nom.trim()}>Créer</button>
            </div>
          </div>
        </div>
      )}
      </>)}

      {/* ═══ TARIFS MO ═══ */}
      {activeTab === 'tarifs-mo' && (
        <div>
          {!tarifsMoLoading && <TarifMoComparatif tarifsMo={tarifsMo} colors={chartColors} />}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button className="btn-primary" style={{ fontSize: 13, padding: '6px 14px' }} onClick={() => setNewTarifMo({ code: '', libelle: '', tarif_ht: '', date_tarif: new Date().toISOString().split('T')[0], concession_id: '' })}>
              + Ajouter un tarif
            </button>
          </div>

          {newTarifMo && (
            <div style={{ background: 'var(--bg-muted)', borderRadius: 8, padding: 12, marginBottom: 12, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              {concessions.length > 1 && (
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>Concession</label>
                  <select value={newTarifMo.concession_id} onChange={e => setNewTarifMo(p => ({ ...p, concession_id: e.target.value }))} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-default)', fontSize: 12 }}>
                    <option value="">—</option>
                    {concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>Code *</label>
                <input placeholder="T1, T2, T3..." value={newTarifMo.code} onChange={e => setNewTarifMo(p => ({ ...p, code: e.target.value.toUpperCase() }))} style={{ width: 80, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-default)', fontSize: 12 }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>Libellé</label>
                <input placeholder="Mécanique, Carrosserie..." value={newTarifMo.libelle} onChange={e => setNewTarifMo(p => ({ ...p, libelle: e.target.value }))} style={{ width: 200, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-default)', fontSize: 12 }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>Tarif HT (€/h)</label>
                <input type="number" step="0.01" placeholder="0,00" value={newTarifMo.tarif_ht} onChange={e => setNewTarifMo(p => ({ ...p, tarif_ht: e.target.value }))} style={{ width: 100, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-default)', fontSize: 12, textAlign: 'right' }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>Date tarif</label>
                <input type="date" value={newTarifMo.date_tarif} onChange={e => setNewTarifMo(p => ({ ...p, date_tarif: e.target.value }))} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-default)', fontSize: 12 }} />
              </div>
              <button className="btn-primary" style={{ fontSize: 12, padding: '6px 12px' }} disabled={!newTarifMo.code.trim()} onClick={async () => {
                try {
                  await api.post('/forfaits/tarifs-mo', newTarifMo);
                  setNewTarifMo(null);
                  loadTarifsMo();
                } catch (e) { alert(e.response?.data?.error || 'Erreur'); }
              }}>Ajouter</button>
              <button className="btn-secondary" style={{ fontSize: 12, padding: '6px 12px' }} onClick={() => setNewTarifMo(null)}>Annuler</button>
            </div>
          )}

          {tarifsMoLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Chargement...</div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    {concessions.length > 1 && <th>Concession</th>}
                    <th style={{ width: 100 }}>Code</th>
                    <th>Libellé</th>
                    <th style={{ width: 110, textAlign: 'right' }}>Tarif HT (€/h)</th>
                    <th style={{ width: 80, textAlign: 'right' }}>TVA</th>
                    <th style={{ width: 110, textAlign: 'right' }}>Tarif TTC</th>
                    <th style={{ width: 110 }}>Date tarif</th>
                    <th style={{ width: 80 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tarifsMo.length === 0 ? (
                    <tr><td colSpan={concessions.length > 1 ? 8 : 7} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>Aucun tarif MO configuré</td></tr>
                  ) : tarifsMo.map(t => (
                    <tr key={t.id}>
                      {concessions.length > 1 && <td>{t.concession_nom || '—'}</td>}
                      <td onClick={() => setEditTarifMo({ id: t.id, field: 'code' })} style={{ cursor: 'pointer' }}>
                        {editTarifMo?.id === t.id && editTarifMo?.field === 'code' ? (
                          <input autoFocus defaultValue={t.code} onBlur={async e => { await api.put(`/forfaits/tarifs-mo/${t.id}`, { ...t, code: e.target.value.toUpperCase() }); setEditTarifMo(null); loadTarifsMo(); }} onKeyDown={e => e.key === 'Enter' && e.target.blur()} style={{ width: 70, padding: '2px 4px', border: '1px solid var(--color-accent)', borderRadius: 4 }} />
                        ) : (
                          <span style={{ fontWeight: 700 }}>{t.code}</span>
                        )}
                      </td>
                      <td style={{ cursor: 'pointer' }} onClick={() => setEditTarifMo({ id: t.id, field: 'libelle' })}>
                        {editTarifMo?.id === t.id && editTarifMo?.field === 'libelle' ? (
                          <input autoFocus defaultValue={t.libelle || ''} onBlur={async e => { await api.put(`/forfaits/tarifs-mo/${t.id}`, { ...t, libelle: e.target.value }); setEditTarifMo(null); loadTarifsMo(); }} onKeyDown={e => e.key === 'Enter' && e.target.blur()} style={{ width: '100%', padding: '2px 4px', border: '1px solid var(--color-accent)', borderRadius: 4 }} />
                        ) : (t.libelle || <span style={{ color: 'var(--text-muted)' }}>—</span>)}
                      </td>
                      <td style={{ textAlign: 'right', cursor: 'pointer' }} onClick={() => setEditTarifMo({ id: t.id, field: 'tarif_ht' })}>
                        {editTarifMo?.id === t.id && editTarifMo?.field === 'tarif_ht' ? (
                          <input autoFocus type="number" step="0.01" defaultValue={t.tarif_ht} onBlur={async e => { await api.put(`/forfaits/tarifs-mo/${t.id}`, { ...t, tarif_ht: parseFloat(e.target.value) || 0 }); setEditTarifMo(null); loadTarifsMo(); }} onKeyDown={e => e.key === 'Enter' && e.target.blur()} style={{ width: 80, padding: '2px 4px', border: '1px solid var(--color-accent)', borderRadius: 4, textAlign: 'right' }} />
                        ) : <strong>{formatPrix(t.tarif_ht)}</strong>}
                      </td>
                      <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--text-secondary)' }}>
                        {formatPrix((parseFloat(t.tarif_ht) || 0) * 0.085)}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>
                        {formatPrix((parseFloat(t.tarif_ht) || 0) * 1.085)}
                      </td>
                      <td style={{ fontSize: 12, cursor: 'pointer' }} onClick={() => setEditTarifMo({ id: t.id, field: 'date_tarif' })}>
                        {editTarifMo?.id === t.id && editTarifMo?.field === 'date_tarif' ? (
                          <input autoFocus type="date" defaultValue={t.date_tarif || ''} onBlur={async e => { await api.put(`/forfaits/tarifs-mo/${t.id}`, { ...t, date_tarif: e.target.value || null }); setEditTarifMo(null); loadTarifsMo(); }} onKeyDown={e => e.key === 'Enter' && e.target.blur()} style={{ padding: '2px 4px', border: '1px solid var(--color-accent)', borderRadius: 4, fontSize: 12 }} />
                        ) : (t.date_tarif ? new Date(t.date_tarif).toLocaleDateString('fr-FR') : <span style={{ color: 'var(--text-muted)' }}>—</span>)}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn-icon" title="Modifier" onClick={() => setEditTarifMo({ id: t.id, field: 'code' })}>✏️</button>
                          <button className="btn-icon delete" title="Supprimer" onClick={async () => { if (!window.confirm('Supprimer ce tarif ?')) return; await api.delete(`/forfaits/tarifs-mo/${t.id}`); loadTarifsMo(); }}>🗑️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ═══ TARIFS CONCURRENCE ═══ */}
      {activeTab === 'concurrence' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <button className="btn-primary" style={{ fontSize: 13, padding: '6px 14px' }} onClick={() => setNewTarifCon({ concurrent: '', prestation: '', tarif_ht: '', commentaire: '', date_releve: new Date().toISOString().split('T')[0], concession_id: '' })}>
              + Ajouter un relevé
            </button>
          </div>

          {newTarifCon && (
            <div style={{ background: 'var(--bg-muted)', borderRadius: 8, padding: 12, marginBottom: 12, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              {concessions.length > 1 && (
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>Concession</label>
                  <select value={newTarifCon.concession_id} onChange={e => setNewTarifCon(p => ({ ...p, concession_id: e.target.value }))} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-default)', fontSize: 12 }}>
                    <option value="">—</option>
                    {concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>Concurrent *</label>
                <input placeholder="Nom du concurrent" value={newTarifCon.concurrent} onChange={e => setNewTarifCon(p => ({ ...p, concurrent: e.target.value }))} style={{ width: 180, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-default)', fontSize: 12 }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>Prestation *</label>
                <input placeholder="Révision 10 000km..." value={newTarifCon.prestation} onChange={e => setNewTarifCon(p => ({ ...p, prestation: e.target.value }))} style={{ width: 200, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-default)', fontSize: 12 }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>Tarif HT</label>
                <input type="number" step="0.01" placeholder="0,00" value={newTarifCon.tarif_ht} onChange={e => setNewTarifCon(p => ({ ...p, tarif_ht: e.target.value }))} style={{ width: 100, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-default)', fontSize: 12, textAlign: 'right' }} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 2 }}>Date relevé</label>
                <input type="date" value={newTarifCon.date_releve} onChange={e => setNewTarifCon(p => ({ ...p, date_releve: e.target.value }))} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-default)', fontSize: 12 }} />
              </div>
              <button className="btn-primary" style={{ fontSize: 12, padding: '6px 12px' }} disabled={!newTarifCon.concurrent.trim() || !newTarifCon.prestation.trim()} onClick={async () => {
                try {
                  await api.post('/forfaits/concurrence', newTarifCon);
                  setNewTarifCon(null);
                  loadTarifsConcurrence();
                } catch (e) { alert(e.response?.data?.error || 'Erreur'); }
              }}>Ajouter</button>
              <button className="btn-secondary" style={{ fontSize: 12, padding: '6px 12px' }} onClick={() => setNewTarifCon(null)}>Annuler</button>
            </div>
          )}

          {tarifsConLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Chargement...</div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    {concessions.length > 1 && <th>Concession</th>}
                    <th>Concurrent</th>
                    <th>Prestation</th>
                    <th style={{ width: 100, textAlign: 'right' }}>Tarif HT</th>
                    <th style={{ width: 100 }}>Date relevé</th>
                    <th>Commentaire</th>
                    <th style={{ width: 80 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tarifsConcurrence.length === 0 ? (
                    <tr><td colSpan={concessions.length > 1 ? 7 : 6} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>Aucun relevé de concurrence</td></tr>
                  ) : tarifsConcurrence.map(t => (
                    <tr key={t.id}>
                      {concessions.length > 1 && <td>{t.concession_nom || '—'}</td>}
                      <td style={{ fontWeight: 600 }}>{t.concurrent}</td>
                      <td>{t.prestation}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatPrix(t.tarif_ht)}</td>
                      <td style={{ fontSize: 12 }}>{t.date_releve ? new Date(t.date_releve).toLocaleDateString('fr-FR') : '—'}</td>
                      <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t.commentaire || '—'}</td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn-icon delete" title="Supprimer" onClick={async () => { if (!window.confirm('Supprimer ce relevé ?')) return; await api.delete(`/forfaits/concurrence/${t.id}`); loadTarifsConcurrence(); }}>🗑️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Forfaits;
